import { useState, useEffect, useMemo, useCallback } from "react";
import { DayPicker } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { format, isSameDay, isWeekend, addMonths, subMonths } from "date-fns";
import { CalendarIcon, ChevronLeft, ChevronRight, X, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color coding for day types
const DAY_COLORS = {
  weekday: "bg-white hover:bg-gray-100 text-gray-900",
  weekend: "bg-orange-100 hover:bg-orange-200 text-orange-800 font-medium",
  holiday: "bg-red-100 hover:bg-red-200 text-red-800 font-medium",
  selected: "bg-[#F37021] text-white hover:bg-[#E06010]",
  today: "border-2 border-[#F37021]"
};

// Legend component - moved outside to avoid re-creation
const CalendarLegend = () => (
  <div className="flex flex-wrap gap-3 text-xs mt-2 pt-2 border-t">
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-white border border-gray-300" />
      <span>Weekday</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300" />
      <span>Weekend</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-red-100 border border-red-300" />
      <span>Holiday</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-[#F37021]" />
      <span>Selected</span>
    </div>
  </div>
);

// Breakdown display component - moved outside
const BreakdownDisplay = ({ breakdown, mode, selectedDatesCount, maxDatesWarning }) => {
  if (!breakdown || breakdown.total_days === 0) return null;
  
  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium">Day Breakdown</span>
        <Badge variant="outline">{breakdown.total_days} days total</Badge>
      </div>
      <div className="flex gap-4 mt-2 text-sm">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-white border" />
          <span>{breakdown.weekday_count} weekdays</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-100 border border-orange-300" />
          <span>{breakdown.weekend_holiday_count} weekend/holidays</span>
        </div>
      </div>
      {mode === "custom" && selectedDatesCount > maxDatesWarning && (
        <div className="mt-2 flex items-center gap-2 text-amber-600 text-xs">
          <AlertTriangle className="w-4 h-4" />
          <span>Large selection ({selectedDatesCount} dates). This may affect pricing significantly.</span>
        </div>
      )}
    </div>
  );
};

export function DateSelectionCalendar({
  mode = "consecutive", // "consecutive" or "custom"
  selectedDates = [],
  onSelectedDatesChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  workOnWeekends = true,
  minDate,
  maxDatesWarning = 30,
  className
}) {
  const [holidays, setHolidays] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  // Fetch Swedish holidays for visible months
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const year = currentMonth.getFullYear();
        const prevYear = year - 1;
        const nextYear = year + 1;
        
        const [current, prev, next] = await Promise.all([
          axios.get(`${API}/swedish-holidays/${year}`).catch(() => ({ data: { holidays: [] } })),
          axios.get(`${API}/swedish-holidays/${prevYear}`).catch(() => ({ data: { holidays: [] } })),
          axios.get(`${API}/swedish-holidays/${nextYear}`).catch(() => ({ data: { holidays: [] } }))
        ]);
        
        const allHolidays = {};
        [...prev.data.holidays, ...current.data.holidays, ...next.data.holidays].forEach(h => {
          allHolidays[h.date] = h.name;
        });
        setHolidays(allHolidays);
      } catch (error) {
        console.error("Failed to fetch holidays:", error);
      }
    };
    fetchHolidays();
  }, [currentMonth]);

  // Calculate breakdown when dates change
  useEffect(() => {
    const calculateBreakdown = async () => {
      if (mode === "custom" && selectedDates.length > 0) {
        try {
          const response = await axios.post(`${API}/calculate-date-breakdown`, null, {
            params: { dates: selectedDates.map(d => format(d, "yyyy-MM-dd")).join(",") }
          });
          setBreakdown(response.data);
        } catch {
          // Calculate locally as fallback
          const weekdays = selectedDates.filter(d => !isWeekend(d) && !holidays[format(d, "yyyy-MM-dd")]).length;
          const weekendHolidays = selectedDates.length - weekdays;
          setBreakdown({
            total_days: selectedDates.length,
            weekday_count: weekdays,
            weekend_holiday_count: weekendHolidays
          });
        }
      } else if (mode === "consecutive" && startDate && endDate) {
        try {
          const response = await axios.post(`${API}/calculate-date-breakdown`, null, {
            params: {
              start_date: format(startDate, "yyyy-MM-dd"),
              end_date: format(endDate, "yyyy-MM-dd"),
              work_on_weekends: workOnWeekends
            }
          });
          setBreakdown(response.data);
        } catch {
          setBreakdown(null);
        }
      } else if (mode === "consecutive" && startDate) {
        // Single date
        const dateStr = format(startDate, "yyyy-MM-dd");
        const isHol = holidays[dateStr];
        const isWknd = isWeekend(startDate);
        setBreakdown({
          total_days: 1,
          weekday_count: isHol || isWknd ? 0 : 1,
          weekend_holiday_count: isHol || isWknd ? 1 : 0
        });
      } else {
        setBreakdown(null);
      }
    };
    calculateBreakdown();
  }, [selectedDates, startDate, endDate, mode, workOnWeekends, holidays]);

  // Day type classifier
  const getDayType = useCallback((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    if (holidays[dateStr]) return "holiday";
    if (isWeekend(date)) return "weekend";
    return "weekday";
  }, [holidays]);

  // Modifiers for react-day-picker
  const modifiers = useMemo(() => {
    const holidayDates = Object.keys(holidays).map(d => new Date(d));
    return {
      holiday: holidayDates,
      weekend: (date) => isWeekend(date) && !holidays[format(date, "yyyy-MM-dd")]
    };
  }, [holidays]);

  const modifiersClassNames = useMemo(() => ({
    holiday: "!bg-red-100 !text-red-800 font-medium",
    weekend: "!bg-orange-100 !text-orange-800",
    selected: "!bg-[#F37021] !text-white"
  }), []);

  // Handle date selection in custom mode
  const handleCustomSelect = useCallback((dates) => {
    if (!dates) {
      onSelectedDatesChange?.([]);
      return;
    }
    
    // dates is an array in multiple mode
    const sortedDates = [...dates].sort((a, b) => a - b);
    onSelectedDatesChange?.(sortedDates);
  }, [onSelectedDatesChange]);

  // Handle date click in consecutive mode
  const handleConsecutiveSelect = useCallback((date) => {
    if (!startDate || (startDate && endDate)) {
      // Start fresh
      onStartDateChange?.(date);
      onEndDateChange?.(null);
    } else {
      // Set end date
      if (date < startDate) {
        onStartDateChange?.(date);
        onEndDateChange?.(startDate);
      } else {
        onEndDateChange?.(date);
      }
    }
  }, [startDate, endDate, onStartDateChange, onEndDateChange]);

  // Remove a selected date in custom mode
  const removeDate = (dateToRemove) => {
    const newDates = selectedDates.filter(d => !isSameDay(d, dateToRemove));
    onSelectedDatesChange?.(newDates);
  };

  // Clear all selections
  const clearAll = () => {
    if (mode === "custom") {
      onSelectedDatesChange?.([]);
    } else {
      onStartDateChange?.(null);
      onEndDateChange?.(null);
    }
  };

  // Get display text
  const getDisplayText = () => {
    if (mode === "custom") {
      if (selectedDates.length === 0) return "Select dates";
      if (selectedDates.length === 1) return format(selectedDates[0], "PPP");
      return `${selectedDates.length} dates selected`;
    } else {
      if (!startDate) return "Select start date";
      if (!endDate) return format(startDate, "PPP");
      return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`;
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !startDate && mode === "consecutive" && "text-muted-foreground",
              selectedDates.length === 0 && mode === "custom" && "text-muted-foreground"
            )}
            data-testid="date-selection-trigger"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {getDisplayText()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-medium">
                {format(currentMonth, "MMMM yyyy")}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Calendar */}
            {mode === "custom" ? (
              <DayPicker
                mode="multiple"
                selected={selectedDates}
                onSelect={handleCustomSelect}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                modifiers={modifiers}
                modifiersClassNames={modifiersClassNames}
                disabled={minDate ? { before: minDate } : undefined}
                className="border rounded-md p-2"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "hidden",
                  nav: "hidden",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                  day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md",
                  day_selected: "bg-[#F37021] text-white hover:bg-[#E06010] hover:text-white focus:bg-[#E06010] focus:text-white",
                  day_today: "border-2 border-[#F37021]",
                  day_outside: "text-muted-foreground opacity-50",
                  day_disabled: "text-muted-foreground opacity-50"
                }}
              />
            ) : (
              <DayPicker
                mode="range"
                selected={{ from: startDate, to: endDate }}
                onSelect={(range) => {
                  onStartDateChange?.(range?.from || null);
                  onEndDateChange?.(range?.to || null);
                }}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                modifiers={modifiers}
                modifiersClassNames={modifiersClassNames}
                disabled={minDate ? { before: minDate } : undefined}
                className="border rounded-md p-2"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "hidden",
                  nav: "hidden",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                  day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md",
                  day_selected: "bg-[#F37021] text-white hover:bg-[#E06010] hover:text-white",
                  day_range_start: "bg-[#F37021] text-white rounded-l-md",
                  day_range_end: "bg-[#F37021] text-white rounded-r-md",
                  day_range_middle: "bg-[#F37021]/20",
                  day_today: "border-2 border-[#F37021]",
                  day_outside: "text-muted-foreground opacity-50",
                  day_disabled: "text-muted-foreground opacity-50"
                }}
              />
            )}
            
            <CalendarLegend />
          </div>
        </PopoverContent>
      </Popover>

      {/* Selected dates list for custom mode */}
      {mode === "custom" && selectedDates.length > 0 && (
        <div className="mt-2">
          <div className="flex justify-between items-center mb-2">
            <Label className="text-sm text-gray-600">Selected Dates ({selectedDates.length})</Label>
            <Button variant="ghost" size="sm" onClick={clearAll} className="text-red-500 h-6 px-2">
              <X className="w-3 h-3 mr-1" /> Clear All
            </Button>
          </div>
          <ScrollArea className="h-32 border rounded-md p-2">
            <div className="flex flex-wrap gap-1">
              {selectedDates.map((date) => {
                const dayType = getDayType(date);
                const holidayName = holidays[format(date, "yyyy-MM-dd")];
                const dateKey = format(date, "yyyy-MM-dd");
                return (
                  <Badge
                    key={dateKey}
                    variant="secondary"
                    className={cn(
                      "cursor-pointer hover:opacity-80",
                      dayType === "holiday" && "bg-red-100 text-red-800",
                      dayType === "weekend" && "bg-orange-100 text-orange-800"
                    )}
                    onClick={() => removeDate(date)}
                    title={holidayName || (dayType === "weekend" ? "Weekend" : "Weekday")}
                  >
                    {format(date, "MMM d")}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      <BreakdownDisplay 
        breakdown={breakdown}
        mode={mode}
        selectedDatesCount={selectedDates.length}
        maxDatesWarning={maxDatesWarning}
      />
    </div>
  );
}

export default DateSelectionCalendar;
